/**
 * ปิดเดือนนี้ (Close Month) — ยุบ flow ปิดเงินเดือน 6-8 คลิกเหลือ 1-2 คลิก.
 *
 * หน้าเดียวรวม: ความพร้อมปิดงวด (preflight) + ตารางเงินสุทธิเทียบเดือนก่อน
 * + ปุ่มเดียว "ปิดงวด + ส่งสลิป" (ConsequencePreview ก่อนยืนยัน) + เอกสารหลังปิด.
 * โหมดละเอียดทีละขั้น (สรุปเวลา/ตรวจก่อนปิด/ทำเงินเดือน) ยังอยู่ครบในแท็บเดิม.
 *
 * Contract (backend คู่ขนาน — fix แล้ว ห้ามเดาเพิ่ม):
 *   GET  /owner/payroll/close-month/preflight?period=YYYY-MM
 *   POST /owner/payroll/close-month { period, acknowledge?, ack_reason? }
 *     → 200 { ok, period, status, sent: { success, failed } }
 *     → 409 { error: "needs_acknowledge", items } (ยืนยันพร้อมเหตุผลแล้วยิงซ้ำ)
 *     → 409 hard block (มี items ที่ override ไม่ได้)
 *
 * หมายเหตุ: ใช้ raw fetch (ไม่ผ่าน apiFetch) เพราะ contract คืน JSON ตรงๆ
 * ที่ root — เขียนแบบทนทั้งสองทรง (root หรือห่อ envelope {ok,data}).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2, AlertCircle, AlertTriangle, Lock, Send, Banknote,
  FileText, FileDown, Users, Wallet, Clock, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Loading } from '../../components/layout/Loading';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { StatCard } from '../../components/ui/StatCard';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import { evaluateCloseGate, resolveAckTarget, type AckContext } from '@payroll/shared';
import { useSettings, useEmployees, type ReadinessItem } from '../../hooks';
import { apiDownload, apiFetch } from '../../lib/api-client';
import { downloadRegistryXlsx } from '../../lib/registry-xlsx';
import { useAuthStore } from '../../lib/store';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

// === Types (ตาม contract) ===

type RunStatus = 'none' | 'draft' | 'previewed' | 'approved' | 'locked' | 'sent';

interface PreflightRow {
  emp_code: string;
  name: string;
  base: number;
  earn_total: number;
  deduct_total: number;
  net: number;
  prior_net: number;
}

interface PreflightReadiness {
  ready: boolean;
  affectsCycleCount: number;
  hardBlockCount: number;
  totalCount: number;
  items: ReadinessItem[];
  dueNow: boolean;
  cutoffDay: number;
  mode: 'attendance' | 'salary_only';
}

interface Preflight {
  period: string;
  readiness: PreflightReadiness;
  run_status: RunStatus;
  reconciled: boolean;
  /** ตัวเลขในหน้านี้เป็นภาพ ณ ตอนสรุปเวลาครั้งล่าสุด */
  summary_computed_at?: string | null;
  summary_stale?: boolean;
  summary_stale_reason?: string | null;
  rows: PreflightRow[];
  totals: { employee_count: number; gross: number; deduct: number; net: number; prior_net: number };
  can_close: boolean;
  salary_only: boolean;
}

/**
 * รหัสพนักงานที่ได้รับผลกระทบจากรายการค้างหนึ่งรายการ
 *
 * backend ใส่มาที่ detail.emp_codes (จำกัด 50 คนแรก) สำหรับรายการที่ผูกกับตัวคน
 * เช่น "คนออกกลางงวดยังไม่ได้ทำเงินงวดสุดท้าย" / "ยังไม่ได้ตั้งฐานเงินเดือน"
 */
function affectedCodes(it: ReadinessItem): string[] {
  const raw = (it.detail as { emp_codes?: unknown } | undefined)?.emp_codes;
  return Array.isArray(raw) ? raw.filter((c): c is string => typeof c === 'string') : [];
}

/** "17 ก.ค. 2569 17:48" — เวลาไทย */
function thaiDateTime(iso: string): string {
  return new Date(iso).toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

type CloseOutcome =
  | { kind: 'ok'; status: string; sent?: { success: number; failed: number } }
  /** HTTP 202 — HR ที่สิทธิ์ปิดงวดเป็น "รออนุมัติ" ยังไม่ได้ปิดจริง */
  | { kind: 'queued'; message?: string }
  | { kind: 'needs_acknowledge'; items: ReadinessItem[] }
  | { kind: 'blocked'; items: ReadinessItem[] };

// === Fetchers (ทนทั้ง JSON ที่ root และห่อ envelope {ok,data}) ===

function authHeaders(json = false): Record<string, string> {
  const jwt = useAuthStore.getState().jwt;
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  };
}

function unwrap<T>(json: unknown): T {
  const j = json as { ok?: unknown; data?: unknown } | null;
  if (j && j.ok === true && j.data && typeof j.data === 'object') return j.data as T;
  return json as T;
}

async function fetchPreflight(period: string): Promise<Preflight> {
  const res = await fetch(
    `${API_BASE}/owner/payroll/close-month/preflight?period=${encodeURIComponent(period)}`,
    { headers: authHeaders() },
  );
  const json: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = (json as { error?: unknown } | null)?.error;
    throw new Error(typeof msg === 'string' ? msg : `โหลดข้อมูลไม่สำเร็จ (HTTP ${res.status})`);
  }
  return unwrap<Preflight>(json);
}

async function postCloseMonth(body: { period: string; acknowledge?: boolean; ack_reason?: string }): Promise<CloseOutcome> {
  const res = await fetch(`${API_BASE}/owner/payroll/close-month`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  const raw = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  // ทนทรง envelope ({ok:false, code, details:{…}}) และทรง root ({error, items})
  const details = raw.details && typeof raw.details === 'object' ? (raw.details as Record<string, unknown>) : {};
  const code = [raw.error, raw.code, details.error].find((v) => typeof v === 'string') as string | undefined;
  // ⭐ hard block ส่งรายการมาที่ details.hardBlockers (หรือ details.readiness.items)
  // ไม่ใช่ items — เดิมอ่านแต่ items เลยได้ค่าว่าง หน้าจอจึงบอกแค่ "ปิดไม่ได้"
  // โดยไม่บอกว่าเพราะอะไร ทั้งที่ backend ส่งเหตุผลมาครบ
  const readiness = details.readiness && typeof details.readiness === 'object'
    ? (details.readiness as Record<string, unknown>)
    : {};
  const itemsRaw = [raw.items, details.items, details.hardBlockers, readiness.items].find((v) => Array.isArray(v));
  const items: ReadinessItem[] = Array.isArray(itemsRaw)
    ? (itemsRaw as ReadinessItem[]).filter((i) => i && typeof i.label === 'string')
    : [];

  if (res.status === 409) {
    if (code === 'needs_acknowledge') return { kind: 'needs_acknowledge', items };
    return { kind: 'blocked', items };
  }
  if (!res.ok) {
    throw new Error(typeof raw.error === 'string' ? raw.error : `เกิดข้อผิดพลาด (HTTP ${res.status})`);
  }
  const data = unwrap<{ status?: string; queued?: boolean; message?: string; sent?: { success: number; failed: number } }>(raw);
  // 🚨 202 = คำขอถูกส่งให้เจ้าของอนุมัติ ยังไม่ได้ปิดงวด — เดิมถือทุก 2xx เป็น
  // "ปิดงวดเรียบร้อยแล้ว" ทำให้ HR เดินจากจอไปโดยคิดว่าจบแล้ว
  if (res.status === 202 || data.queued === true || data.status === 'queued') {
    return { kind: 'queued', message: data.message };
  }
  return { kind: 'ok', status: data.status ?? 'sent', sent: data.sent };
}

// === Labels / routes ===

const RUN_STATUS_BADGE: Record<RunStatus, { label: string; cls: string }> = {
  none: { label: 'ยังไม่เริ่ม', cls: 'bg-gray-100 text-gray-600' },
  draft: { label: 'ร่าง', cls: 'bg-slate-100 text-slate-700' },
  previewed: { label: 'ร่าง', cls: 'bg-slate-100 text-slate-700' },
  approved: { label: 'อนุมัติแล้ว', cls: 'bg-blue-100 text-blue-700' },
  locked: { label: 'ปิดงวดแล้ว', cls: 'bg-yellow-100 text-yellow-700' },
  sent: { label: 'ส่งสลิปแล้ว', cls: 'bg-green-100 text-green-700' },
};

// ลิงก์ "ไปแก้" ต่อรายการ — เช็คกับ route จริงใน App.tsx แล้ว
// unresolved = คนที่ยังมีการ์ดผิดปกติค้าง (ลาฉุกเฉินแยกเป็น emergency_leave_pending_review แล้ว) → /hr/escalations (fb#351)
// escalation_pending = กิจกรรมผิดปกติ → หน้าจัดการ /hr/escalations (override ได้)
const ITEM_ROUTE: Record<string, string> = {
  forgot_payroll: '/owner/cycle#payroll',
  missing_salary_base: '/hr/employees',
  emergency_leave_pending_review: '/approvals#emergency',
  unresolved: '/hr/escalations',
  escalation_pending: '/hr/escalations',
  leave_pending: '/approvals',
  ot_pending: '/approvals',
  missing_summary: '/owner/cycle#reconcile',
  terminated_no_finalpay: '/hr/people#termination',
  net_variance: '/owner/cycle#payroll',
  unpaired_line: '/hr/people#all',
  missing_start_date: '/hr/people#all',
  resigned_no_end_date: '/hr/people#all',
};

function getLast6Periods(): string[] {
  const out: string[] = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

const baht = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// === รายการความพร้อมหนึ่งแถว + ลิงก์ไปแก้ ===

function ReadinessRow({ it }: { it: ReadinessItem }) {
  const isRed = it.severity === 'red';
  const Icon = isRed ? AlertCircle : AlertTriangle;
  const to = ITEM_ROUTE[it.key];
  // fb#351 — ชื่อคนในรายการให้เห็นเลย ไม่ต้องไปตามหาอีกหน้า (query เดียวกันทุกแถว cache ร่วม)
  const { data: emps } = useEmployees() as { data: Array<{ emp_code: string; first_name?: string | null; last_name?: string | null; nickname?: string | null }> | undefined };
  const codes = affectedCodes(it);
  const nameOf = (c: string) => {
    const e = emps?.find((x) => x.emp_code === c);
    const full = e ? [e.first_name, e.last_name].filter(Boolean).join(' ') : '';
    return `${full || c}${e?.nickname ? ` (${e.nickname})` : ''}`;
  };
  return (
    <div className={`flex items-center gap-3 border-l-4 ${isRed ? 'border-l-red-500' : 'border-l-amber-400'} bg-white rounded-r-lg px-3 py-2.5`}>
      <Icon className={`w-5 h-5 shrink-0 ${isRed ? 'text-red-600' : 'text-amber-600'}`} />
      <div className="flex-1 min-w-0">
        <span className="text-sm">{it.label}</span>
        {codes.length > 0 && (
          <p className="text-xs text-gray-500 mt-0.5">
            {codes.slice(0, 10).map(nameOf).join(' · ')}
            {it.count > 10 && ` · และอีก ${it.count - 10} คน`}
          </p>
        )}
      </div>
      <span className="text-sm font-semibold tabular-nums">{it.count}</span>
      {to && (
        <Link to={to} className="text-sm font-medium text-brand-700 hover:underline shrink-0">
          ไปแก้
        </Link>
      )}
    </div>
  );
}

/** Content-only — ใช้เป็นแท็บ default ใน PayrollCycle hub. */
interface SsoExportJson {
  period: string;
  company_name: string;
  account_no: string;
  branch_seq: string;
  rate_percent: number;
  due_date: string;
  rows: Array<{ citizen_id: string; first_name: string; last_name: string; wage: number; contribution: number }>;
  summary: { employeeCount: number; totalWage: number; totalContribution: number; employeeContribution: number; employerContribution: number };
}

export function CloseMonthContent() {
  const [period, setPeriod] = useState<string>(getLast6Periods()[0] ?? '');
  const [submitting, setSubmitting] = useState(false);
  // fb#600 วันที่โอนในไฟล์ .txt ธนาคาร (= วันจ่ายเงินเดือน) — ว่าง = วันนี้ตามเดิม
  const [transferDate, setTransferDate] = useState('');
  // กองเรื่องเตือน (เหลือง) พับไว้เป็นค่าเริ่มต้น — กางเองเมื่ออยากดู
  const [showMinor, setShowMinor] = useState(false);
  // dialog ใส่เหตุผล (409 needs_acknowledge) — ยิงซ้ำพร้อม acknowledge:true
  const [ackOpen, setAckOpen] = useState(false);
  const [ackReason, setAckReason] = useState('');
  const [ackItems, setAckItems] = useState<ReadinessItem[]>([]);
  /** งวด+บริษัทของคำขอที่ทำให้กล่องนี้เปิด — ยืนยันแล้วต้องยิงกลับไปที่ "อันนี้" */
  const [ackCtx, setAckCtx] = useState<AckContext | null>(null);
  // 409 hard block — รายการที่ override ไม่ได้
  const [blockItems, setBlockItems] = useState<ReadinessItem[]>([]);

  const toast = useToast();
  const consequence = useConsequence();
  const qc = useQueryClient();
  const settings = useSettings();
  const tenantId = useAuthStore((s) => s.profile?.tenant_id ?? null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    // ⭐ ต้องมี tenant ใน key — ไม่งั้นสลับบริษัทแล้วยังเห็นตัวเลขบริษัทเก่าค้าง
    queryKey: ['close-month-preflight', tenantId, period],
    queryFn: () => fetchPreflight(period),
    staleTime: 30_000,
  });

  // สลับบริษัทระหว่างที่กล่องเหตุผลเปิดค้าง = ทิ้งคำยืนยันนั้นทันที
  // (เหตุผลที่พิมพ์ไว้สำหรับบริษัทหนึ่ง ห้ามถูกใช้ปิดงวดของอีกบริษัท)
  useEffect(() => {
    if (ackCtx && ackCtx.tenantId !== tenantId) {
      setAckOpen(false);
      setAckReason('');
      setAckItems([]);
      setAckCtx(null);
      setBlockItems([]);
    }
  }, [tenantId, ackCtx]);

  async function submitClose(body: { period: string; acknowledge?: boolean; ack_reason?: string }) {
    // บริบทที่ยิงออกไป — ใช้ตัดสินตอนคำตอบกลับมาว่ายัง "เรื่องเดียวกัน" อยู่ไหม
    const sentCtx: AckContext = { period: body.period, tenantId };
    setSubmitting(true);
    try {
      const out = await postCloseMonth(body);

      // 🚨 ระหว่างรอคำตอบ ผู้ใช้อาจสลับบริษัทไปแล้ว — คำตอบของบริษัทเก่าห้าม
      // ไปสั่งการอะไรต่อในบริบทใหม่ (งวดเปลี่ยนไม่เป็นไร เพราะเราผูก ackCtx ไว้แล้ว)
      if (useAuthStore.getState().profile?.tenant_id !== sentCtx.tenantId) {
        toast.error('สลับบริษัทระหว่างดำเนินการ — ยกเลิกคำสั่งนี้เพื่อความปลอดภัย ลองใหม่อีกครั้ง');
        return;
      }

      if (out.kind === 'ok') {
        setAckOpen(false);
        setAckReason('');
        setAckCtx(null);
        setBlockItems([]);
        if (out.sent) {
          toast.success(
            `ปิดงวด ${sentCtx.period} และส่งสลิปแล้ว — สำเร็จ ${out.sent.success} คน` +
            (out.sent.failed > 0 ? ` · ส่งไม่สำเร็จ ${out.sent.failed} คน (ส่งซ้ำได้)` : ''),
          );
        } else {
          toast.success(`ปิดงวด ${sentCtx.period} เรียบร้อยแล้ว`);
        }
        await qc.invalidateQueries({ queryKey: ['close-month-preflight'] });
      } else if (out.kind === 'queued') {
        // ยังไม่ได้ปิดงวด — รอเจ้าของกดอนุมัติ ห้ามบอกว่าเสร็จแล้ว
        setAckOpen(false);
        setAckReason('');
        setAckCtx(null);
        setBlockItems([]);
        toast.info(out.message ?? `ส่งคำขอปิดงวด ${sentCtx.period} ให้เจ้าของอนุมัติแล้ว — งวดยังไม่ปิดจนกว่าเจ้าของจะกดยืนยัน`);
        await qc.invalidateQueries({ queryKey: ['close-month-preflight'] });
      } else if (out.kind === 'needs_acknowledge') {
        setAckItems(out.items);
        setAckCtx(sentCtx);   // ← ผูกกล่องไว้กับงวด+บริษัทที่ยิงไป
        setAckOpen(true);
      } else {
        setAckOpen(false);
        setAckCtx(null);
        setBlockItems(out.items);
        toast.error(
          out.items.length > 0
            ? `ปิดงวด ${sentCtx.period} ไม่ได้ — ต้องเคลียร์ ${out.items.map((i) => i.label).join(' · ')} ก่อน`
            : `ปิดงวด ${sentCtx.period} ไม่ได้ — มีรายการที่ต้องแก้ให้เสร็จก่อน`,
        );
        await refetch();
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMainAction() {
    if (!data) return;
    const isResendOnly = data.run_status === 'locked';
    const redItems = data.readiness.items.filter((i) => i.severity === 'red' && i.count > 0);
    const ok = await consequence({
      title: isResendOnly ? `ส่งสลิปงวด ${period}` : `ปิดงวด ${period} + ส่งสลิป`,
      intro: isResendOnly
        ? 'งวดนี้ปิดแล้วแต่ยังส่งสลิปไม่ครบ — ระบบจะส่งสลิปให้พนักงานทาง LINE (คนที่ได้รับแล้วจะไม่ได้รับซ้ำ)'
        : 'เมื่อกดยืนยัน ระบบจะปิดงวดเงินเดือนนี้และส่งสลิปให้พนักงานทันที',
      lines: [
        { label: 'จ่ายพนักงาน', value: `${data.totals.employee_count} คน` },
        { label: 'รวมจ่ายสุทธิ', value: `฿${baht(data.totals.net)}`, tone: 'money', emphasis: true },
      ],
      effects: isResendOnly
        ? ['สลิปจะถูกส่งให้พนักงานทาง LINE ทันที', 'กดส่งซ้ำได้ถ้ายังมีคนตกหล่น']
        : [
            'ล็อกงวดนี้ — ตัวเลขเงินเดือนแก้ไขไม่ได้อีก',
            'สลิปจะถูกส่งให้พนักงานทาง LINE ทันที',
            // บอกล่วงหน้าว่ายังมีอีก 1 ขั้น จะได้ไม่งงว่าทำไมกดยืนยันแล้วเด้งกล่องอีก
            ...(needsReason
              ? ['ขั้นถัดไประบบจะให้พิมพ์เหตุผลที่ปิดทั้งที่ยังมีเรื่องค้าง — เก็บไว้เป็นหลักฐาน']
              : []),
          ],
      alerts: redItems.map((i) => ({
        level: 'warn' as const,
        title: `${i.label} (${i.count} รายการ)`,
        detail: 'ยังค้างอยู่ — ปิดงวดได้แต่ต้องยืนยันพร้อมเหตุผล',
      })),
      danger: !isResendOnly,
      confirmText: isResendOnly ? 'ส่งสลิป' : 'ปิดงวด + ส่งสลิป',
    });
    if (!ok) return;
    await submitClose({ period });
  }

  async function submitAcknowledge() {
    if (!ackReason.trim()) {
      toast.error('กรุณาระบุเหตุผลก่อนยืนยัน');
      return;
    }
    // 🚨 ต้องยิงกลับไปที่ "งวดที่ทำให้กล่องนี้เปิด" ไม่ใช่ `period` ที่หน้าจอถืออยู่
    // (ผู้ใช้อาจเปลี่ยนดรอปดาวน์ระหว่างรอคำตอบ = ปิดผิดงวดโดยไม่รู้ตัว)
    const target = resolveAckTarget(ackCtx, tenantId);
    if (!target.ok) {
      toast.error(
        target.reason === 'tenant_changed'
          ? 'สลับบริษัทระหว่างยืนยัน — ยกเลิกคำสั่งนี้เพื่อความปลอดภัย'
          : 'คำยืนยันหมดอายุ — กรุณากดปิดงวดใหม่อีกครั้ง',
      );
      setAckOpen(false);
      setAckCtx(null);
      return;
    }
    await submitClose({ period: target.period, acknowledge: true, ack_reason: ackReason.trim() });
  }

  async function download(path: string, filename: string) {
    try {
      await apiDownload(path, filename);
    } catch (e) {
      toast.error(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    }
  }

  // fb#478 — ไฟล์นำส่ง .txt เป็น TIS-620 ตามที่ e-Service ต้องการ เปิดใน Notepad แล้วไทยเพี้ยน
  // → ออกข้อมูลชุดเดียวกันเป็น Excel ให้เปิดตรวจ/เก็บแฟ้มได้ (ตัวเลขจากเซิร์ฟเวอร์ ไม่คำนวณใหม่ที่จอ)
  async function downloadSsoXlsx() {
    try {
      const d = await apiFetch<SsoExportJson>(`/owner/export/sso?period=${encodeURIComponent(period)}&type=json`);
      const XLSX = await import('xlsx');
      const [y, m] = d.period.split('-').map(Number);
      const periodLabel = new Date(y!, m! - 1, 1).toLocaleDateString('th-TH', { month: 'long', year: 'numeric' });
      const aoa: (string | number)[][] = [
        ['แบบรายการแสดงการส่งเงินสมทบ (สปส.1-10)'],
        ['ชื่อสถานประกอบการ', d.company_name],
        ['เลขที่บัญชีนายจ้าง', d.account_no],
        ['ลำดับที่สาขา', d.branch_seq.padStart(6, '0')],
        ['งวดค่าจ้าง', periodLabel],
        ['อัตราเงินสมทบ (%)', d.rate_percent],
        ['กำหนดชำระ', new Date(`${d.due_date}T00:00:00`).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })],
        [],
        ['ลำดับ', 'เลขประจำตัวประชาชน', 'ชื่อ', 'นามสกุล', 'ค่าจ้างที่คิดเงินสมทบ', 'เงินสมทบลูกจ้าง'],
        ...d.rows.map((r, i) => [i + 1, r.citizen_id, r.first_name, r.last_name, r.wage, r.contribution]),
        ['', '', '', 'รวม', d.summary.totalWage, d.summary.employeeContribution],
        [],
        ['จำนวนผู้ประกันตน (คน)', d.summary.employeeCount],
        ['เงินสมทบฝ่ายลูกจ้าง', d.summary.employeeContribution],
        ['เงินสมทบฝ่ายนายจ้าง', d.summary.employerContribution],
        ['รวมเงินสมทบที่ต้องชำระ', d.summary.totalContribution],
      ];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [{ wch: 22 }, { wch: 20 }, { wch: 20 }, { wch: 22 }, { wch: 20 }, { wch: 16 }];
      // เลขบัตร/เลขบัญชีเป็นข้อความ (กัน Excel แปลงเป็น 1.1E+12) · เงินแสดง 2 ตำแหน่ง
      const money = (r: number, c: number) => { const cell = ws[XLSX.utils.encode_cell({ r, c })]; if (cell) cell.z = '#,##0.00'; };
      const lastTableRow = 9 + d.rows.length; // แถวรวม
      for (let r = 9; r <= lastTableRow; r++) { money(r, 4); money(r, 5); }
      for (let r = lastTableRow + 3; r <= lastTableRow + 5; r++) money(r, 1);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'สปส.1-10');
      XLSX.writeFile(wb, `sso110-${period}.xlsx`);
    } catch (e) {
      toast.error(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    }
  }

  // ไฟล์โอนธนาคาร: format จาก settings PRIMARY_BANK (ถ้าตั้งไว้) ไม่งั้น csv
  // fb#143 — ค่าเริ่มต้นฝั่งเซิร์ฟเวอร์คือ 'other' (= CSV ทั่วไป) แต่จอเคยส่ง format=other ตรง ๆ →
  // ปุ่มขึ้น "ไฟล์โอน (เฉพาะบัญชี)" ไม่มีชื่อธนาคาร กดแล้วเซิร์ฟเวอร์ปฏิเสธ = TN ไม่มีทางได้ไฟล์โอนจากหน้านี้
  const primaryBank = settings.data?.PRIMARY_BANK?.trim().toLowerCase() ?? '';
  const bankFormat = primaryBank === 'kbank' || primaryBank === 'scb' ? primaryBank : 'csv';
  const bankLabel = bankFormat === 'kbank' ? 'กสิกรไทย' : bankFormat === 'scb' ? 'ไทยพาณิชย์' : '';

  // fb#69 — ไฟล์ธนาคารโอนได้เฉพาะบัญชีธนาคารเดียวกัน: ออกไฟล์เฉพาะบัญชี{ธนาคาร} แล้วบอกว่ายังมีใครต้องโอนแยก
  async function downloadBankFileSameBank() {
    try {
      const res = await fetch(
        `${API_BASE}/owner/payroll/bank-transfer?period=${encodeURIComponent(period)}&format=${encodeURIComponent(bankFormat)}&scope=same_bank${transferDate ? `&date=${transferDate}` : ''}`,
        { headers: authHeaders() },
      );
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { msg = ((await res.json()) as { error?: string }).error ?? msg; } catch { /* ไม่ใช่ JSON */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bank-transfer-${period}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const other = Number(res.headers.get('X-Other-Bank-Count') ?? '0');
      if (other > 0) {
        toast.info(`ไฟล์โอน${bankLabel}พร้อมแล้ว — อีก ${other} คนใช้บัญชีต่างธนาคาร ไม่อยู่ในไฟล์นี้ กด "รายชื่อโอนแยก" เพื่อโอนผ่าน K BIZ/K PLUS`);
      } else {
        toast.success(`ไฟล์โอน${bankLabel}พร้อมแล้ว — ทุกคนใช้บัญชี${bankLabel} ไม่มีใครต้องโอนแยก`);
      }
    } catch (e) {
      toast.error(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    }
  }

  if (isLoading) return <Loading />;
  if (isError || !data) {
    return (
      <ErrorState
        detail={error instanceof Error ? error.message : undefined}
        onRetry={() => void refetch()}
      />
    );
  }

  // แดง = บล็อกการปิดงวดจริง (affectsCycle) · เหลือง = แค่เตือน ไม่เคยบล็อก
  // ก่อนถึงรอบปิดงวด backend ลดรายการลงเวลา/ลา/OT เป็นเหลืองอยู่แล้ว → กองเหลือง
  // มักยาวจนกลบตัวที่ต้องลงมือจริง จึงพับไว้ใต้ปุ่มแทนการเรียงต่อกันทั้งหมด
  const visibleItems = data.readiness.items.filter((i) => i.count > 0);
  const blockingItems = visibleItems.filter((i) => i.severity === 'red');
  const minorItems = visibleItems.filter((i) => i.severity !== 'red');
  const badge = RUN_STATUS_BADGE[data.run_status] ?? RUN_STATUS_BADGE.none;
  const isLocked = data.run_status === 'locked';
  const isSent = data.run_status === 'sent';
  const showDocs = isLocked || isSent;

  // ⭐ ด่านฝั่งจอต้องไม่เข้มกว่าเซิร์ฟเวอร์ — backend แบ่งของค้างเป็น 2 ชั้น:
  //   · hardBlock  = ปิดไม่ได้จริง (ลืมทำเงินเดือน / ไม่มีฐานเงินเดือน / สรุปเวลาไม่สด)
  //   · แดงธรรมดา = ปิดได้ "ถ้ายืนยันพร้อมเหตุผล" → POST ตอบ 409 needs_acknowledge
  //                 แล้วเด้ง dialog ให้พิมพ์เหตุผล (ลง audit)
  // เดิมปุ่มถูกปิดเมื่อ !can_close (= readiness.ready เท่านั้น) → พอมีใบลา/OT ค้าง
  // แค่ใบเดียวปุ่มก็เทา ทำให้ 409 ไม่มีวันถูกยิง กล่องเหตุผลจึงไม่มีวันโผล่ =
  // งวดที่มีของค้างปิดไม่ได้ตลอดกาล (TN ริชเชสค้าง มิ.ย.–ส.ค. ด้วยเหตุนี้).
  const hardBlockers = visibleItems.filter((i) => i.hardBlock);
  /** แดงที่ไม่ใช่ hardBlock = ปิดได้ถ้าระบุเหตุผล (ใบลา/OT รออนุมัติ ฯลฯ) */
  const overridableItems = blockingItems.filter((i) => !i.hardBlock);
  // กติกาปุ่มอยู่ที่ @payroll/shared ตัวเดียว — เทสยิงฟังก์ชันเดียวกับที่จอเรียก
  const gate = evaluateCloseGate({
    runStatus: data.run_status,
    canClose: data.can_close,
    hardBlockCount: data.readiness.hardBlockCount,
    submitting,
  });
  const hasHardBlock = gate.mode === 'blocked';
  const needsReason = gate.willAskReason;
  const ctaDisabled = gate.disabled;

  return (
    <div className="space-y-4 max-w-3xl">
      {/* ① หัวหน้า: เลือกงวด + สถานะ */}
      <div className="card p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[10rem]">
            <label className="label">เลือกงวด</label>
            {/* ล็อกไว้ระหว่างกำลังปิดงวด/รอพิมพ์เหตุผล — เปลี่ยนงวดกลางคันคือ
                ต้นทางของการปิดผิดงวด และทำให้ตัวเลขบนจอไม่ตรงกับที่กำลังจะล็อก */}
            <select
              value={period}
              onChange={(e) => { setPeriod(e.target.value); setBlockItems([]); }}
              className="input"
              disabled={submitting || ackOpen}
            >
              {getLast6Periods().map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <span className={`badge ${badge.cls} mb-2`}>{badge.label}</span>
        </div>
        {!data.readiness.dueNow && !isSent && !isLocked && (
          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            ยังไม่ถึงรอบปิดงวด — ปกติปิดราววันที่ {data.readiness.cutoffDay} ของเดือน
          </p>
        )}
        {data.salary_only && (
          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
            <Wallet className="w-3.5 h-3.5 shrink-0" />
            โหมดเงินเดือนล้วน — จ่ายเต็มตามฐาน ไม่นำการลงเวลา/ลา/OT มาคิด
          </p>
        )}
        {/* ตัวเลขทั้งหน้ามาจาก "สรุปเวลาเข้างาน" ที่ปั๊มไว้ ไม่ได้คำนวณสด — บอกให้ชัด
            ว่าเป็นภาพเมื่อไหร่ (ระบบสรุปใหม่ให้อัตโนมัติแล้วถ้าข้อมูลขยับ) */}
        {!data.salary_only && data.summary_computed_at && (
          <p className={`text-xs mt-2 flex items-center gap-1.5 ${data.summary_stale ? 'text-amber-700' : 'text-gray-500'}`}>
            <Clock className="w-3.5 h-3.5 shrink-0" />
            {data.summary_stale
              ? `ตัวเลขเป็นภาพ ณ ${thaiDateTime(data.summary_computed_at)} และยังไม่ตามข้อมูลล่าสุด (${data.summary_stale_reason ?? 'มีข้อมูลเปลี่ยนหลังจากนั้น'}) — กดสรุปเวลาใหม่ที่หน้า "สรุปเวลาเข้างาน" ก่อนปิดงวด`
              : `ตัวเลขสรุปจากการลงเวลา ณ ${thaiDateTime(data.summary_computed_at)} (ตรงกับข้อมูลปัจจุบัน)`}
          </p>
        )}
      </div>

      {/* ② โซนความพร้อม — โชว์เฉพาะตัวที่บล็อกจริง ที่เหลือพับไว้ */}
      {blockingItems.length === 0 ? (
        !isSent && (
          <div className="card p-4 flex items-center gap-3 border-l-4 border-l-brand-500">
            <CheckCircle2 className="w-6 h-6 text-brand-600 shrink-0" />
            <div>
              <p className="font-medium">พร้อมปิดงวด ✓</p>
              <p className="text-sm text-gray-500">ไม่มีเรื่องค้างที่กระทบรอบเงินเดือน</p>
            </div>
          </div>
        )
      ) : (
        <div className="space-y-4">
          {/* แยก "ต้องแก้จริง" ออกจาก "ค้างอยู่แต่ปิดได้ถ้ามีเหตุผล" — เดิมรวมกอง
              เดียวใต้หัวข้อ "ต้องแก้ก่อนปิดงวด" ทั้งที่ครึ่งหนึ่ง override ได้ */}
          {hardBlockers.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-700">
                ต้องแก้ก่อนปิดงวด ({hardBlockers.length} รายการ)
              </h3>
              {hardBlockers.map((it) => <ReadinessRow key={it.key} it={it} />)}
            </div>
          )}
          {overridableItems.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-700">
                ค้างอยู่ — ปิดงวดได้ถ้าระบุเหตุผล ({overridableItems.length} รายการ)
              </h3>
              {overridableItems.map((it) => <ReadinessRow key={it.key} it={it} />)}
            </div>
          )}
        </div>
      )}

      {/* fb#254 — รอบก่อนหลายคนไม่มีเลขบัญชีเต็ม ทั้งที่กรอกโปรไฟล์ครบ → ตรวจก่อนปิดงวดได้จากไฟล์ทะเบียน */}
      {!isSent && (
        <div className="card p-4 flex flex-wrap items-center gap-3">
          <Banknote className="w-5 h-5 text-gray-500 shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <p className="font-medium text-sm">ตรวจเลขบัญชีรับเงินเดือนก่อนปิดงวด</p>
            <p className="text-xs text-gray-500">ไฟล์ทะเบียนพนักงานมีคอลัมน์ "ตรวจเลขบัญชี" ท้ายสุด — คนที่ขึ้น "มีแค่ 4 ตัวท้าย" หรือ "ยังไม่มี" จะไม่มีเลขบัญชีในไฟล์โอน เติมเลขเต็มในไฟล์แล้วอัปโหลดกลับที่หน้านำเข้าข้อมูลได้</p>
          </div>
          <button
            type="button"
            onClick={() => void downloadRegistryXlsx('/owner/employees/org-export', 'employees-registry.xlsx').catch((e) => toast.error(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`))}
            className="btn-secondary"
          >
            ดาวน์โหลดทะเบียน + เลขบัญชี
          </button>
        </div>
      )}

      {minorItems.length > 0 && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowMinor((v) => !v)}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            {showMinor
              ? <ChevronUp className="w-4 h-4 shrink-0" />
              : <ChevronDown className="w-4 h-4 shrink-0" />}
            เรื่องเตือนไว้ ไม่บล็อกการปิดงวด ({minorItems.length} รายการ)
          </button>
          {showMinor && minorItems.map((it) => <ReadinessRow key={it.key} it={it} />)}
        </div>
      )}

      {/* hard block จากการกดปิด (409) — รายการที่ข้ามไม่ได้ */}
      {blockItems.length > 0 && (
        <div className="card p-4 border-l-4 border-l-red-600 bg-red-50 space-y-2">
          <p className="font-medium text-red-700">ปิดงวดไม่ได้ — ต้องแก้รายการต่อไปนี้ก่อน (ข้ามไม่ได้)</p>
          {blockItems.map((it) => <ReadinessRow key={it.key} it={it} />)}
        </div>
      )}

      {/* ③ การ์ดสรุป + ตารางเงินสุทธิ */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={Users} label="จำนวนคน" value={data.totals.employee_count} />
        <StatCard
          icon={Wallet}
          label="รวมจ่ายสุทธิ"
          value={`฿${data.totals.net.toLocaleString('th-TH')}`}
          hint={data.totals.prior_net > 0
            ? `เดือนก่อน ฿${data.totals.prior_net.toLocaleString('th-TH')}`
            : undefined}
        />
      </div>

      {data.rows.length === 0 ? (
        <div className="card">
          <EmptyState
            title="ยังไม่มีรายการเงินเดือนในงวดนี้"
            detail="ตรวจว่าเลือกงวดถูกต้อง และมีพนักงานที่ตั้งฐานเงินเดือนแล้ว"
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">พนักงาน</th>
                  <th className="text-right px-3 py-2 font-medium">เงินได้</th>
                  <th className="text-right px-3 py-2 font-medium">เงินหัก</th>
                  <th className="text-right px-3 py-2 font-medium">สุทธิ</th>
                  <th className="text-right px-3 py-2 font-medium">เทียบเดือนก่อน</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.rows.map((r) => {
                  const delta = r.prior_net > 0 ? ((r.net - r.prior_net) / r.prior_net) * 100 : null;
                  const big = delta !== null && Math.abs(delta) > 20;
                  return (
                    <tr key={r.emp_code}>
                      <td className="px-3 py-2">
                        <div>{r.name || r.emp_code}</div>
                        <div className="text-xs text-gray-400 font-mono">{r.emp_code}</div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.earn_total.toLocaleString('th-TH')}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-700">{r.deduct_total.toLocaleString('th-TH')}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.net.toLocaleString('th-TH')}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${big ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>
                        {delta === null
                          ? '—'
                          : `${delta > 0 ? '▲ +' : delta < 0 ? '▼ ' : ''}${delta.toFixed(1)}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                <tr>
                  <td className="px-3 py-2">รวม {data.totals.employee_count} คน</td>
                  <td className="px-3 py-2 text-right tabular-nums">{data.totals.gross.toLocaleString('th-TH')}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-700">{data.totals.deduct.toLocaleString('th-TH')}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-brand-900">{data.totals.net.toLocaleString('th-TH')}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                    {data.totals.prior_net > 0 ? data.totals.prior_net.toLocaleString('th-TH') : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ④ ปุ่มใหญ่ปุ่มเดียว */}
      {isSent ? (
        <div className="card p-4 flex items-center gap-3 border-l-4 border-l-green-500">
          <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
          <div>
            <p className="font-medium">งวดนี้ปิดและส่งสลิปแล้ว</p>
            <p className="text-sm text-gray-500">ดาวน์โหลดเอกสารประจำเดือนได้จากการ์ดด้านล่าง</p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <button
            onClick={handleMainAction}
            disabled={ctaDisabled}
            className="btn-cta w-full py-3 text-base"
          >
            {isLocked ? <Send className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
            {submitting
              ? 'กำลังดำเนินการ…'
              : isLocked
                ? 'ส่งสลิป'
                : needsReason
                  ? 'ปิดงวด + ส่งสลิป (ต้องระบุเหตุผล)'
                  : 'ปิดงวด + ส่งสลิป'}
          </button>
          {/* ข้อความต้องบอกว่า "ต้องไปแก้ตรงไหน" ไม่ใช่แค่บอกว่าทำไม่ได้ */}
          {!isLocked && hasHardBlock && (
            <p className="text-xs text-red-600 text-center">
              ยังปิดงวดไม่ได้ — ต้องเคลียร์{' '}
              {hardBlockers.map((i) => `${i.label} (${i.count})`).join(' · ')} ก่อน
            </p>
          )}
          {needsReason && (
            <p className="text-xs text-amber-700 text-center">
              ยังมีเรื่องค้างที่กระทบงวดนี้ — ปิดงวดได้ แต่ระบบจะให้ระบุเหตุผลไว้เป็นหลักฐานก่อน
            </p>
          )}
        </div>
      )}

      {/* ⑤ เอกสารเดือนนี้ — หลังปิดงวด/ส่งสลิปแล้ว */}
      {showDocs && (
        <div className="card p-4">
          <div className="font-semibold mb-1">เอกสารเดือนนี้</div>
          <p className="text-sm text-gray-500 mb-3">ไฟล์สำหรับโอนเงิน ยื่นภาษี และเก็บเข้าแฟ้ม</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {/* fb#600 — เปิดใน Excel แล้วเลข 0 หน้าบัญชีหาย → ออกเป็น .xlsx ทุกช่องเป็นข้อความ (ยอดเงินเป็นตัวเลข) */}
            <button
              onClick={() => void downloadRegistryXlsx(
                `/owner/payroll/bank-transfer?period=${encodeURIComponent(period)}&format=csv`,
                `bank-transfer-${period}.xlsx`,
                { sheetName: 'ไฟล์โอนเงิน', numericCols: [5] },
              ).catch((e) => toast.error(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`))}
              className="btn-secondary justify-start"
            >
              <Banknote className="w-4 h-4" /> ไฟล์โอนเงินธนาคาร (Excel · มีเลขบัญชีทุกคน)
            </button>
            {bankFormat !== 'csv' && (
              <>
                {/* fb#69 — ไฟล์ธนาคารเฉพาะบัญชีธนาคารเดียวกัน + รายชื่อบัญชีต่างธนาคารโอนแยก */}
                <div className="flex flex-col gap-1">
                  <button onClick={() => void downloadBankFileSameBank()} className="btn-secondary justify-start">
                    <Banknote className="w-4 h-4" /> ไฟล์โอน{bankLabel} (.txt ส่งธนาคาร · เฉพาะบัญชี{bankLabel})
                  </button>
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    วันที่โอนในไฟล์ .txt
                    <input
                      type="date"
                      value={transferDate}
                      onChange={(e) => setTransferDate(e.target.value)}
                      className="input text-base py-1 w-auto"
                    />
                    {!transferDate && <span className="text-xs text-gray-400">ว่าง = วันนี้</span>}
                  </label>
                </div>
                <button
                  onClick={() => void downloadRegistryXlsx(
                    `/owner/payroll/bank-transfer?period=${encodeURIComponent(period)}&format=csv&scope=other_bank&bank=${encodeURIComponent(bankFormat)}`,
                    `bank-transfer-other-bank-${period}.xlsx`,
                    { sheetName: 'โอนแยก', numericCols: [5] },
                  ).catch((e) => toast.error(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`))}
                  className="btn-secondary justify-start"
                >
                  <FileDown className="w-4 h-4" /> บัญชีต่างธนาคาร — รายชื่อโอนแยก (Excel)
                </button>
              </>
            )}
            <button
              onClick={() => void download(`/owner/export/pnd1?period=${encodeURIComponent(period)}&type=txt`, `pnd1-${period}.txt`)}
              className="btn-secondary justify-start"
            >
              <FileDown className="w-4 h-4" /> ภ.ง.ด.1 (ไฟล์ยื่นออนไลน์)
            </button>
            <button
              onClick={() => void download(`/owner/export/pnd1?period=${encodeURIComponent(period)}&type=pdf`, `pnd1-${period}.pdf`)}
              className="btn-secondary justify-start"
            >
              <FileText className="w-4 h-4" /> ภ.ง.ด.1 (PDF)
            </button>
            <button
              onClick={() => void download(`/owner/export/sso?period=${encodeURIComponent(period)}&type=txt`, `sso110-${period}.txt`)}
              className="btn-secondary justify-start"
            >
              <FileDown className="w-4 h-4" /> สปส.1-10 (ไฟล์นำส่ง .txt · แนบ e-Service)
            </button>
            <button onClick={() => void downloadSsoXlsx()} className="btn-secondary justify-start">
              <FileDown className="w-4 h-4" /> สปส.1-10 (Excel · เปิดอ่าน/ตรวจ)
            </button>
            <button
              onClick={() => void download(`/owner/export/sso?period=${encodeURIComponent(period)}&type=pdf`, `sso110-${period}.pdf`)}
              className="btn-secondary justify-start"
            >
              <FileText className="w-4 h-4" /> สปส.1-10 (PDF)
            </button>
            <button
              onClick={() => void download(`/owner/export/payroll?period=${encodeURIComponent(period)}`, `payroll-${period}.csv`)}
              className="btn-secondary justify-start"
            >
              <FileDown className="w-4 h-4" /> รายงานเงินเดือน (CSV)
            </button>
          </div>
        </div>
      )}

      {/* ⑥ ทางเข้าโหมดละเอียด (แท็บเดิม 3 ขั้น ยังอยู่ครบ) */}
      <p className="text-center text-sm pb-2">
        <Link to="/owner/cycle#reconcile" className="text-gray-500 hover:text-brand-700 hover:underline">
          โหมดละเอียด (ทีละขั้น)
        </Link>
      </p>

      {/* Dialog ใส่เหตุผล — 409 needs_acknowledge แล้วยิงซ้ำพร้อม acknowledge:true */}
      <Modal
        open={ackOpen}
        onClose={() => { if (!submitting) { setAckOpen(false); setAckReason(''); setAckCtx(null); } }}
        title={`ยืนยันปิดงวด ${ackCtx?.period ?? period} ทั้งที่มีเรื่องค้าง`}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            ยังมีเรื่องค้างที่กระทบรอบเงินเดือนของงวด{' '}
            <span className="font-semibold">{ackCtx?.period ?? period}</span> — ปิดงวดได้
            แต่ต้องระบุเหตุผล (ระบบบันทึกไว้ในประวัติการใช้งาน)
          </p>
          {ackItems.length > 0 && (
            <div className="space-y-1.5">
              {ackItems.map((it) => (
                <div key={it.key} className="text-sm bg-amber-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <span className="flex-1 min-w-0">{it.label}</span>
                    <span className="font-semibold tabular-nums">{it.count}</span>
                  </div>
                  {/* ⭐ โชว์ว่า "ใครโดน" — การให้เหตุผลไม่ได้ทำให้ยอดถูกต้องขึ้น
                      คนกดต้องเห็นชื่อคนที่จะถูกปิดทับ ไม่ใช่เห็นแค่ตัวเลขรวม */}
                  {affectedCodes(it).length > 0 && (
                    <div className="mt-1 pl-6 text-xs text-amber-900/80 break-words">
                      กระทบ: {affectedCodes(it).slice(0, 12).join(', ')}
                      {affectedCodes(it).length > 12 ? ` และอีก ${affectedCodes(it).length - 12} คน` : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div>
            <label className="label">เหตุผลที่ปิดงวดทั้งที่มีเรื่องค้าง</label>
            <textarea
              className="input"
              rows={3}
              value={ackReason}
              onChange={(e) => setAckReason(e.target.value)}
              placeholder="เช่น ใบลาที่ค้างจะนำไปคิดในงวดหน้า"
            />
          </div>
          <div className="flex gap-2">
            <button
              className="btn-secondary flex-1"
              onClick={() => { setAckOpen(false); setAckReason(''); }}
              disabled={submitting}
            >
              ยกเลิก
            </button>
            <button
              className="btn-danger flex-1"
              onClick={() => void submitAcknowledge()}
              disabled={submitting || !ackReason.trim()}
            >
              {submitting ? 'กำลังดำเนินการ…' : 'ยืนยันปิดงวด'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
